// R1b-β — Tabbed People & Access shell.
// Composes AdminAppShell + tab strip + one of three tab bodies driven by URL.
// URL is the source of truth: /admin/people-roles/{users|roles|authority}
// direct URL load lands on the correct tab; browser back/forward navigate
// between tabs; breadcrumbs + back button + Alt+← all work per tab.
import React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Users as UsersIcon, ScrollText, Shield } from "lucide-react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import UsersTab from "./tabs/UsersTab";
import RolesTab from "./tabs/RolesTab";
import AuthorityTab from "./tabs/AuthorityTab";

const TABS = [
  { key: "users",     label: "Users",             icon: UsersIcon,   Body: UsersTab,     testid: "people-roles-tab-users" },
  { key: "roles",     label: "Roles & Permissions", icon: ScrollText, Body: RolesTab,     testid: "people-roles-tab-roles" },
  { key: "authority", label: "Authority Matrix",  icon: Shield,     Body: AuthorityTab, testid: "people-roles-tab-authority" },
];

export default function PeopleRolesShell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tab: tabParam } = useParams();
  const current = TABS.find((t) => t.key === tabParam) || TABS[0];
  const CurrentBody = current.Body;

  return (
    <AdminAppShell
      activeNav="people-access"
      title="People & Access"
      subtitle="Users · Roles · Authority Matrix"
      breadcrumbs={[
        { label: "Owner Command Center", to: "/owner" },
        { label: "People & Access", to: "/admin/people-roles/users" },
        { label: current.label, to: null },
      ]}
      user={user}
    >
      <div>
      {/* B2a (Finding 004): the tab strip wraps on narrow screens so every tab (incl. Authority Matrix) is visible. */}
      <div
        role="tablist"
        aria-label="People & Access sections"
        data-testid="people-roles-tablist"
        style={{
          display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 20,
          borderBottom: "1px solid #E8E4D9",
        }}
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = t.key === current.key;
          return (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-controls={`people-roles-panel-${t.key}`}
              tabIndex={isActive ? 0 : -1}
              data-testid={t.testid}
              onClick={() => navigate(`/admin/people-roles/${t.key}`)}
              onKeyDown={(e) => {
                if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                  e.preventDefault();
                  const dir = e.key === "ArrowRight" ? 1 : -1;
                  const idx = TABS.findIndex((x) => x.key === current.key);
                  const next = TABS[(idx + dir + TABS.length) % TABS.length];
                  navigate(`/admin/people-roles/${next.key}`);
                }
              }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "10px 16px", minHeight: 44,
                background: isActive ? "#FFFFFF" : "transparent",
                border: 0, borderBottom: isActive ? "2px solid #B8892B" : "2px solid transparent",
                marginBottom: "-1px",
                fontFamily: '"Inter Tight", system-ui, sans-serif',
                fontSize: 13, fontWeight: isActive ? 600 : 500,
                color: isActive ? "#0F0F10" : "#4A4A4C",
                cursor: "pointer", whiteSpace: "nowrap",
                transition: "color 0.15s ease, background 0.15s ease",
              }}
            >
              <Icon size={14} aria-hidden="true" />
              {t.label}
            </button>
          );
        })}
      </div>
      </div>

      <div
        role="tabpanel"
        id={`people-roles-panel-${current.key}`}
        aria-labelledby={current.testid}
        data-testid={`people-roles-panel-${current.key}`}
      >
        <CurrentBody />
      </div>
    </AdminAppShell>
  );
}
